import { FC, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getTestCenters,
  getJobPrefrences,
  updateTestCentresPreference,
} from "../../../../core/services/adi-service";

import { TestCenterDataModel } from "../../../../core/models/test-center-data-model";
import { JobPreferenceDataModel } from "../../../../core/models/job-preference-data-model";
import Button from "../../shared/elements/Button";
import Notification from "../../shared/overlays/Notification";
import toast from "react-hot-toast";
import PageHeader from "../../shared/elements/PageHeader";
import Skeleton from "@mui/material/Skeleton";
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useHistory } from "react-router-dom";
import { TestCentres } from "./TestCentres";

interface FormValues {
  test_centres: TestCenterDataModel[];
}

const TestCentrePreference: FC = () => {
  const [testCenters, setTestCenters] = useState<TestCenterDataModel[]>([]);
  const [jobPreference, setJobPreference] = useState<JobPreferenceDataModel | null>(null);
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const redirectToSettings = () => {
    history.push("/ADI/settings/overview");
  };

  const validationSchema = Yup.object().shape({
    test_centres: Yup.array()
      .of(
        Yup.object().shape({
          test_center_id: Yup.string().required("Test centre is required"),
        })
      )
      .min(1, "At least one test centre is required"),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      test_centres: [],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      setStatus("");

      if (!jobPreference) {
        setSubmitting(false);
        return;
      }

      const selectedCentres = testCenters
        .filter((tc) =>
          values.test_centres.some((selected: any) => selected.test_center_id === tc.id)
        )
        .map((testCenter) => {
          const existing = jobPreference.adi_test_centers.find(
            (a) => a.test_center_id === testCenter.id
          );
          return {
            id: existing?.id || "",
            test_center_id: testCenter.id,
            postal_code: testCenter.postal_code,
            test_center_name: testCenter.test_center_name,
            lat: testCenter.lat,
            long: testCenter.long,
          };
        });

      const toastId = toast.custom(() => (
        <Notification
          colour="gray"
          title="Saving test centres..."
          description=""
          Icon={ExclamationCircleIcon}
        />
      ));

      updateTestCentresPreference({ adi_test_centers: selectedCentres })
        .then(() => {
          toast.custom(() => (
            <Notification
              colour="green"
              title="Saved successfully"
              description=""
              Icon={CheckCircleIcon}
            />
          ), { id: toastId });

          redirectToSettings();
        })
        .catch(() => {
          toast.custom(() => (
            <Notification
              colour="red"
              title="Failed to save test centres"
              description=""
              Icon={ExclamationTriangleIcon}
            />
          ), { id: toastId });

          setStatus("Error saving test centres.");
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const addTestCentre = () => {
    if (testCenters.length <= formik.values.test_centres.length) return;
    const updated = [...formik.values.test_centres, new TestCenterDataModel()];
    formik.setFieldValue("test_centres", updated);
  };

  const deleteTestCentre = (index: number) => {
    const updated = [...formik.values.test_centres];
    updated.splice(index, 1);
    formik.setFieldValue("test_centres", updated);
  };

  const onTestCentreChange = (e: any, index: number) => {
    const updated = [...formik.values.test_centres];
    updated[index].test_center_id = e.value;
    updated[index].test_center_name = e.label;
    formik.setFieldValue("test_centres", updated);
  };

  const getAvailableOptions = () => {
    return testCenters
      .filter((tc) => !formik.values.test_centres.some((sel: any) => sel.test_center_id === tc.id))
      .map((tc) => ({
        label: tc.test_center_name,
        value: tc.id,
      }));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getTestCenters(), getJobPrefrences()]).then(([tcRes, prefRes]) => {
      const testCentres = tcRes.data.results;
      const preferenceData = prefRes.data.results;

      setTestCenters(testCentres);
      setJobPreference(preferenceData);
      formik.setFieldValue("test_centres", preferenceData.adi_test_centers);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <div className="mb-5">
        <PageHeader title="Test Centres" backToURL="/ADI/settings/overview" />
      </div>
      <div className="bg-white p-6 rounded shadow">
        <form onSubmit={formik.handleSubmit}>
          {formik.status && <p className="text-sm text-red-600">{formik.status}</p>}

          {loading ? (
            <Skeleton height={100} />
          ) : (
            <TestCentres
              values={formik.values.test_centres}
              label="Please select all the test centres you cover"
              onChange={onTestCentreChange}
              options={getAvailableOptions()}
              isDisabled={formik.isSubmitting}
              deleteTestCentre={deleteTestCentre}
              addTestCentre={addTestCentre}
              errorMsg={formik.errors.test_centres}
            />
          )}

          <div className="my-5 grid grid-cols-2 gap-2">
            {loading ? <Skeleton width={150} height={50} animation="wave"></Skeleton> :
              <Button
                type='submit'
                title='Save'
                disabled={formik.isSubmitting}
                colour="yellow"
              >
                <span className='indicator-label'> Save</span>
              </Button>}

            {loading ? <Skeleton width={150} height={50} animation="wave"></Skeleton> :
              <Button
                disabled={formik.isSubmitting}
                onClick={() => redirectToSettings()}
                colour="outline"
                size="large"
              >
                <span className='indicator-label'> Cancel</span>
              </Button>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestCentrePreference;
